/**
 * A theory note, as a student reads it.
 *
 * Two surfaces draw it and must not differ: the exam's "Read Theory" drawer,
 * and the curriculum console's preview — an author checking an upload should
 * be looking at exactly what a student will. So it is one component, and it
 * renders the fields of `theoryNoteSchema` and nothing else.
 *
 * Every text field goes through `MathText`, which turns `$…$` into KaTeX and
 * leaves the rest as text. No HTML is interpreted, so an uploaded file cannot
 * put markup or a link on a student's screen.
 */
import type { TheoryNoteContent } from '@cwy/config/contracts';
import { MathText } from '@/components/exam/MathText';

function Label({ children }: { children: string }) {
  return (
    <h4
      className="mb-1.5 text-fs-10 font-bold uppercase tracking-[0.08em]"
      style={{ color: 'var(--ink-3)' }}
    >
      {children}
    </h4>
  );
}

function Bullets({ items }: { items: string[] }) {
  return (
    <ul
      className="flex list-disc flex-col gap-1 pl-5 text-sm"
      style={{ color: 'var(--text-body)' }}
    >
      {items.map((item, i) => (
        <li key={i}>
          <MathText>{item}</MathText>
        </li>
      ))}
    </ul>
  );
}

export function TheoryNoteView({
  note,
  scopeNote,
}: {
  note: TheoryNoteContent;
  /** Said under the title — e.g. that this is the whole topic's note. */
  scopeNote?: string;
}) {
  return (
    <article className="flex flex-col gap-4 text-sm leading-relaxed">
      <header>
        <h3 className="text-base font-bold" style={{ color: 'var(--text-strong)' }}>
          <MathText>{note.title}</MathText>
        </h3>
        {scopeNote && (
          <p className="mt-0.5 text-xs" style={{ color: 'var(--ink-3)' }}>
            {scopeNote}
          </p>
        )}
        {note.summary && (
          <p className="mt-2" style={{ color: 'var(--ink-2)' }}>
            <MathText>{note.summary}</MathText>
          </p>
        )}
      </header>

      {note.sections.map((section, i) => (
        <section key={i}>
          <h4 className="mb-1 font-bold" style={{ color: 'var(--text-body)' }}>
            <MathText>{section.heading}</MathText>
          </h4>
          <p className="whitespace-pre-line" style={{ color: 'var(--text-body)' }}>
            <MathText>{section.body}</MathText>
          </p>
          {section.points && (
            <div className="mt-1.5">
              <Bullets items={section.points} />
            </div>
          )}
        </section>
      ))}

      {note.formulas && note.formulas.length > 0 && (
        <section>
          <Label>Formulas</Label>
          <ul className="flex flex-col gap-1.5">
            {note.formulas.map((formula, i) => (
              <li
                key={i}
                className="rounded-lg px-3 py-2"
                style={{ background: 'var(--paper-sunk)' }}
              >
                <span className="text-xs font-semibold" style={{ color: 'var(--ink-2)' }}>
                  {formula.label}
                </span>
                <div className="mt-0.5">
                  <MathText>{`$${formula.expression}$`}</MathText>
                </div>
                {formula.note && (
                  <p className="mt-0.5 text-xs" style={{ color: 'var(--ink-3)' }}>
                    <MathText>{formula.note}</MathText>
                  </p>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {note.examples && note.examples.length > 0 && (
        <section>
          <Label>Worked examples</Label>
          <ol className="flex flex-col gap-3">
            {note.examples.map((example, i) => (
              <li
                key={i}
                className="rounded-lg p-3"
                style={{ border: '1px solid var(--border-subtle)' }}
              >
                <p className="font-semibold" style={{ color: 'var(--text-body)' }}>
                  <MathText>{example.question}</MathText>
                </p>
                <ol
                  className="mt-1.5 flex list-decimal flex-col gap-1 pl-5"
                  style={{ color: 'var(--ink-2)' }}
                >
                  {example.steps.map((step, j) => (
                    <li key={j}>
                      <MathText>{step}</MathText>
                    </li>
                  ))}
                </ol>
                {example.answer && (
                  <p className="mt-1.5 font-semibold" style={{ color: 'var(--green-text)' }}>
                    <MathText>{example.answer}</MathText>
                  </p>
                )}
              </li>
            ))}
          </ol>
        </section>
      )}

      {note.keyPoints && note.keyPoints.length > 0 && (
        <section>
          <Label>Key points</Label>
          <Bullets items={note.keyPoints} />
        </section>
      )}

      {note.commonMistakes && note.commonMistakes.length > 0 && (
        <section>
          <Label>Common mistakes</Label>
          <Bullets items={note.commonMistakes} />
        </section>
      )}

      {note.remember && (
        <p
          className="rounded-lg px-3 py-2 font-semibold"
          style={{ background: 'var(--brand-lime-soft)', color: 'var(--brand-ink)' }}
        >
          <MathText>{note.remember}</MathText>
        </p>
      )}
    </article>
  );
}
